import { useEffect, useRef, useState } from 'react'
import { useAdminApi } from '../api'
import { t } from '../i18n'
import { useLang } from '../LangContext'
import dialogStyles from '../../styles/Dialog.module.css'
import { Icon } from '../../styles/Icon'

export interface UserFormValues {
  email: string
  nombre_visible: string
  roles: string[]
  categorias_asignadas: string[] | null
  equipo_ids: string[] | null
}

interface Equipo {
  id: string
  nombre: string
}

interface Temporada {
  id: string
  estado: 'abierta' | 'cerrada'
}

interface Props {
  initial?: Partial<UserFormValues> & { id?: string }
  onSave: (values: UserFormValues) => Promise<void>
  onClose: () => void
}

const ROLES = ['admin', 'director', 'coordinador', 'entrenador']
const CATEGORIAS = ['eskola', 'f7', 'f11']

export function UserFormModal({ initial, onSave, onClose }: Props) {
  const api = useAdminApi()
  const lang = useLang()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [equipos, setEquipos] = useState<Equipo[]>([])
  const [equiposCargados, setEquiposCargados] = useState(false)
  const initialRoles = initial?.roles ?? ['entrenador']
  const [values, setValues] = useState<UserFormValues>({
    email: initial?.email ?? '',
    nombre_visible: initial?.nombre_visible ?? '',
    roles: initialRoles,
    categorias_asignadas: initialRoles.includes('coordinador') ? initial?.categorias_asignadas ?? [] : [],
    equipo_ids: initialRoles.includes('entrenador') ? initial?.equipo_ids ?? [] : null,
  })

  useEffect(() => {
    dialogRef.current?.showModal()
    // Issue #23: un entrenador puede tener que asignarse a un equipo de cualquier temporada abierta,
    // no solo la primera; antes solo se cargaba una, y la lista podía quedar vacía sin explicación.
    api.get('/catalogo/temporadas').then(async (temporadas: Temporada[]) => {
      const abiertas = temporadas.filter((temporada) => temporada.estado === 'abierta')
      const listas = await Promise.all(
        abiertas.map((temporada) => api.get(`/catalogo/temporadas/${temporada.id}/equipos`)),
      )
      const porId = new Map<string, Equipo>()
      for (const lista of listas as Equipo[][]) {
        for (const equipo of lista) porId.set(equipo.id, equipo)
      }
      setEquipos(Array.from(porId.values()))
      setEquiposCargados(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Todos los roles se combinan libremente, incluido admin (revertida su exclusividad el 2026-09-24).
  function handleRolToggle(rol: string, checked: boolean) {
    const roles = checked ? [...values.roles, rol] : values.roles.filter((r) => r !== rol)
    setValues({
      ...values,
      roles,
      categorias_asignadas: roles.includes('coordinador') ? values.categorias_asignadas : [],
      equipo_ids: roles.includes('entrenador') ? (values.equipo_ids ?? []) : null,
    })
  }

  function handleCategoriaToggle(categoria: string, checked: boolean) {
    const current = values.categorias_asignadas ?? []
    setValues({
      ...values,
      categorias_asignadas: checked ? [...current, categoria] : current.filter((c) => c !== categoria),
    })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    await onSave(values)
  }

  return (
    <dialog ref={dialogRef} onClose={onClose} className={dialogStyles.dialog}>
      <form onSubmit={handleSubmit}>
        <label>
          {t('email', lang)}
          <input
            type="email"
            required
            value={values.email}
            onChange={(e) => setValues({ ...values, email: e.target.value })}
          />
        </label>
        <label>
          {t('nombre', lang)}
          <input
            required
            value={values.nombre_visible}
            onChange={(e) => setValues({ ...values, nombre_visible: e.target.value })}
          />
        </label>
        <fieldset>
          <legend>{t('rol', lang)}</legend>
          {ROLES.map((rol) => (
            <label key={rol}>
              <input
                type="checkbox"
                checked={values.roles.includes(rol)}
                onChange={(e) => handleRolToggle(rol, e.target.checked)}
              />
              {rol}
            </label>
          ))}
        </fieldset>

        {values.roles.includes('coordinador') && (
          <fieldset>
            <legend>{t('categoriaAsignada', lang)}</legend>
            {CATEGORIAS.map((categoria) => (
              <label key={categoria}>
                <input
                  type="checkbox"
                  checked={values.categorias_asignadas?.includes(categoria) ?? false}
                  onChange={(e) => handleCategoriaToggle(categoria, e.target.checked)}
                />
                {categoria}
              </label>
            ))}
          </fieldset>
        )}

        {values.roles.includes('entrenador') && (
          <fieldset>
            <legend>{t('equiposAsignados', lang)}</legend>
            {equiposCargados && equipos.length === 0 && <p>{t('sinEquiposParaAsignar', lang)}</p>}
            {equipos.map((equipo) => (
              <label key={equipo.id}>
                <input
                  type="checkbox"
                  checked={values.equipo_ids?.includes(equipo.id) ?? false}
                  onChange={(e) => {
                    const current = values.equipo_ids ?? []
                    setValues({
                      ...values,
                      equipo_ids: e.target.checked
                        ? [...current, equipo.id]
                        : current.filter((id) => id !== equipo.id),
                    })
                  }}
                />
                {equipo.nombre}
              </label>
            ))}
          </fieldset>
        )}

        <div className="modal-actions">
          <button type="submit" disabled={values.roles.length === 0}><Icon name="guardar" />{t('guardar', lang)}</button>
          <button type="button" onClick={() => dialogRef.current?.close()}>
            <Icon name="cancelar" />{t('cancelar', lang)}
          </button>
        </div>
      </form>
    </dialog>
  )
}
